import type { ChipTone } from '@/design';
import { formatDateTime } from '@/domains/survey';
import type {
  IListField,
  IListFieldCatalogue,
  IListFieldDraw,
  IListFieldProvenance,
  IListFieldQuestionRef,
  ListFieldKind,
  ListFieldSource,
  ListImportHold,
  ListImportOutcome,
  ListImportState,
  ListOperator,
  ReportRunState,
  SavedViewVisibility
} from './api/lists-contracts';
import { formatDay } from './components/days';
import { formatMoney } from './components/sec-adv-values';

/**
 * The list engine's words (SR-230; design
 * `docs/superpowers/specs/2026-10-08-lists-reports-import-ui.md` §2) — every word Lists, Reports, Import and Export layouts draw for a code: a value by its kind,
 * a source, an operator, a date token, a visibility, a view's mark, a run's and an import's state
 * with its chip's tone, a row's outcome, a hold, a skipped column's reason, a formatting tone and a
 * survey's tab. **Nothing on those screens spells a code to a reader**, and a code this module has
 * no word for is drawn as spelled — never dropped, never mapped to a near neighbour — since
 * survey-api adds vocabulary on its own schedule (the work items' rule).
 *
 * Words, not a mirror — with three exceptions, each of which moves with its C# (the
 * `COLUMN_DEFAULTS` precedent, README's mirrors table):
 *
 * - `OPERATORS_BY_KIND` mirrors `ListOperators.For` (`Application/Lists/ListQueryBinder.cs`) — the
 *   operators each kind takes, the blank pair first. It decides which operators a builder offers;
 *   survey-api still judges every leaf (400 at `query.criteria…op`).
 * - The date tokens (`DATE_WORDS`, `DATE_N_FORMS`) mirror `ListDateTokens` — the relative dates,
 *   with Salesforce's definitions (weeks from Sunday, calendar quarters, the business zone
 *   America/New_York). The words say what survey-api resolves; nothing here resolves a token.
 * - `LIST_SURVEYS` mirrors the six surveys of `ListFieldRules.StaffFieldOwners`, in its order.
 */

function wordOf(table: Readonly<Record<string, string>>, code: string): string {
  return table[code] ?? code;
}

// ---- Surveys ---------------------------------------------------------------------------------

export interface IListSurvey {
  code: string;
  label: string;
  /** Its staff fields' code prefix — `fa.notes` (`StaffFieldOwners`; both Canadas share `ca`). */
  fields?: string;
}

/** The surveys a list reads, as the tabs name them — a mirror of `ListFieldRules`' six. A
 *  deployment that does not hold one answers its catalogue 404, and the tab says so. */
export const LIST_SURVEYS: readonly IListSurvey[] = [
  { code: 'FA', label: 'FA', fields: 'fa' },
  { code: 'TEAMS', label: 'Teams', fields: 'teams' },
  { code: 'FSP', label: 'FSP', fields: 'fsp' },
  { code: 'RIA_FIRM', label: 'RIA firms', fields: 'ria' },
  { code: 'CANADA', label: 'Canada', fields: 'ca' },
  { code: 'CANADA_FR', label: 'Canada (FR)', fields: 'ca' }
];

/** survey-api's cross-survey catalogue (`ListQueryBinder.AllSurveys`): a person in two surveys is
 *  two rows, and only the shared fields are offered. */
/** A survey's notes log, its `<prefix>.notes` staff field (SR-494), or null for a survey that
 *  owns no staff fields. */
export function notesLogCode(survey: string): Nullable<string> {
  const fields = LIST_SURVEYS.find((one) => one.code === survey)?.fields;
  return fields ? `${fields}.notes` : null;
}

export const ALL_SURVEYS: IListSurvey = { code: 'all', label: 'All surveys' };

/** The Teams survey's two paths as its *Team path* chip names them (§3.2, the client's note on
 *  LST-04) — survey-api's path codes, the words this app's; a view naming another path draws it as
 *  spelled. */
export const TEAM_PATH_CHOICES: readonly IListSurvey[] = [
  { code: 'WIRES', label: 'Wirehouse' },
  { code: 'INDEPENDENT', label: 'Independent' }
];

/** The survey whose rows a Team path chip narrows. */
export const TEAMS_SURVEY = 'TEAMS';

/** A survey's tab name; a code no tab knows as spelled. */
export function listSurveyLabel(code: string): string {
  if (code === ALL_SURVEYS.code) return ALL_SURVEYS.label;
  return LIST_SURVEYS.find((survey) => survey.code === code)?.label ?? code;
}

// ---- Sources -----------------------------------------------------------------------------------

const SOURCE_WORDS: Record<string, string> = {
  system: 'Record',
  answer: 'Survey questions',
  internal: 'Staff fields',
  figure: 'Figures',
  placement: 'Rankings',
  roster: 'Advisors & principals',
  adv: 'SEC ADV',
  disclosure: 'Disclosures',
  assignment: 'Assignment',
  fact: 'Prior cycle',
  dd: 'Due diligence',
  outreach: 'Outreach',
  metrics: 'Metrics updates'
};

/** The columns sheet's group for a source. The profile fields are `system` fields, grouped by
 *  survey-api under that source; their sub-heading is the sheet's (task 2). */
export function sourceLabel(source: ListFieldSource): string {
  return wordOf(SOURCE_WORDS, source);
}

/**
 * The profile run of survey-api's fixed fields (`ListFieldRules.FixedFields`, from Name to Phone),
 * which it groups under `system` beside the record's own: the columns sheet heads them "Profile"
 * (§2). Presentation only — a code missing here stays under Record, and nothing is dropped.
 */
export const PROFILE_FIELDS: ReadonlySet<string> = new Set([
  'sys.display_name',
  'sys.first_name',
  'sys.last_name',
  'sys.firm_name',
  'sys.legal_firm_name',
  'sys.team_name',
  'sys.city',
  'sys.state',
  'sys.office_email',
  'sys.phone'
]);

export const PROFILE_LABEL = 'Profile';

/** One heading of the catalogue as the columns sheet and the builder's field list draw it. */
export interface ICatalogueSection {
  key: string;
  title: string;
  fields: readonly IListField[];
  /** The catalogue's survey code, `all` for the cross-survey one — what a field's provenance
   *  line names as its own survey. */
  survey?: string;
}

/** The catalogue's headings, with how many fields of theirs an administrator hid (SR-462). */
export type CatalogueSections = ICatalogueSection[] & { hidden: number };

/**
 * Whether the pickers offer a field (SR-462): an administrator hides some, by the field or by its
 * origin, and survey-api marks them `hidden`. One the caller already uses (`held` — a view's column,
 * a condition's field) stays, so it is still named and can be taken off.
 */
export function isOffered(field: IListField, held?: ReadonlySet<string>): boolean {
  return !field.hidden || (held?.has(field.code) ?? false);
}

/**
 * The catalogue in its groups, in survey-api's order, each under its source's words (§2) — the
 * `system` group split in two, the record's own fields under "Record" and the profile run under
 * "Profile" — and the `dd` group by meeting (`ddSections`). `keep` narrows the fields (the
 * filterable ones, for a filter); a hidden field is left out unless `held` (`isOffered`), and
 * counted in the answer's `hidden`, which a picker says beside its search; a heading left with none
 * is not drawn.
 */
export function catalogueSections(
  catalogue: Nullable<IListFieldCatalogue>,
  keep: (field: IListField) => boolean = () => true,
  held?: ReadonlySet<string>
): CatalogueSections {
  const sections: CatalogueSections = Object.assign([], { hidden: 0 });
  const survey = catalogue?.survey;
  for (const group of catalogue?.groups ?? []) {
    const kept = group.fields.filter(keep);
    const fields = kept.filter((field) => isOffered(field, held));
    sections.hidden += kept.length - fields.length;
    if (group.source === 'system') {
      const record = fields.filter((field) => !PROFILE_FIELDS.has(field.code));
      const profile = fields.filter((field) => PROFILE_FIELDS.has(field.code));
      if (record.length > 0) sections.push({ key: 'system', title: sourceLabel('system'), fields: record, survey });
      if (profile.length > 0) sections.push({ key: 'profile', title: PROFILE_LABEL, fields: profile, survey });
    } else if (group.source === 'dd') {
      sections.push(...ddSections(fields, survey));
    } else if (fields.length > 0) {
      sections.push({ key: group.source, title: sourceLabel(group.source), fields, survey });
    }
  }
  return sections;
}

/** The meetings a `dd.<kind>.` code names, in the order the picker heads them (SR-525, G1). */
const DD_MEETING_GROUPS: ReadonlyArray<{ kind: string; title: string }> = [
  { kind: 'dd_call', title: 'DD call' },
  { kind: 'follow_up_call', title: 'Follow-up call' },
  { kind: 'in_person_1', title: 'In-person Round 1' },
  { kind: 'in_person_2', title: 'In-person Round 2' },
  { kind: 'in_person_3', title: 'In-person Round 3' }
];

/**
 * Due diligence's fields by meeting (SR-525, G1) — "Due diligence · DD call", "· Follow-up call",
 * "· In-person Round 1–3" by their `dd.<kind>.` prefix, then "· Other" (the status, the outreach,
 * the next meeting), keyed `dd` as the whole group was. Presentation only: the prefix is read for
 * its heading, as `PROFILE_FIELDS` is, and a group holding none of a meeting draws one heading.
 */
function ddSections(fields: readonly IListField[], survey: string | undefined): ICatalogueSection[] {
  const title = sourceLabel('dd');
  const byMeeting = DD_MEETING_GROUPS.map(({ kind, title: meeting }) => ({
    key: `dd.${kind}`,
    title: `${title} · ${meeting}`,
    fields: fields.filter((field) => field.code.startsWith(`dd.${kind}.`)),
    survey
  })).filter((section) => section.fields.length > 0);
  const claimed = new Set(byMeeting.flatMap((section) => section.fields.map((field) => field.code)));
  const other = fields.filter((field) => !claimed.has(field.code));
  return [
    ...byMeeting,
    ...(other.length > 0
      ? [{ key: 'dd', title: byMeeting.length > 0 ? `${title} · Other` : title, fields: other, survey }]
      : [])
  ];
}

/** The prefix of a repeat member's slot cell, `rep.<slot>.<question>` (SR-408's R5) — read off
 *  the code for its words alone, like `PROFILE_FIELDS`. */
const REPEAT_SLOT_PREFIX = 'rep.';

/**
 * What a column's header tooltip says of its field (§3.5): where its value comes from, and for an
 * answer its question number — "Survey question 39a" — and, where the list does not edit it (a grid's
 * summary, a computed answer — SR-403, a follow-up — SR-408), that it is not corrected here. A
 * repeat member's slot says which it is; `fact` is FA's prior-cycle research (SR-408's R10).
 */
export function fieldSourceNote(
  field: Pick<IListField, 'code' | 'number' | 'editable'>,
  source: ListFieldSource
): string {
  switch (source) {
    case 'answer':
      if (field.code.startsWith(REPEAT_SLOT_PREFIX)) {
        return 'Survey question, one row of a repeated group — not corrected in a list';
      }
      return `Survey question${field.number ? ` ${field.number}` : ''}${field.editable ? '' : ' — not corrected in a list'}`;
    case 'internal':
      return 'Staff field';
    case 'figure':
      return 'Figure';
    case 'placement':
      return 'Ranking placement';
    case 'fact':
      return 'Prior cycle — Salesforce research';
    case 'system':
      return PROFILE_FIELDS.has(field.code) ? PROFILE_LABEL : 'Record';
    default:
      return sourceLabel(source);
  }
}

// ---- Provenance (SR-420) ----------------------------------------------------------------------

/** survey-api's `ListFieldOrigins` in words: where a field's value comes from, finer than its
 *  group — a profile column, a BrokerCheck-managed staff field. */
const ORIGIN_WORDS: Record<string, string> = {
  record: 'Record',
  profile: 'Profile',
  answer: 'Survey question',
  staff: 'Staff field',
  brokercheck: 'BrokerCheck',
  derived: 'Derived from figures',
  figure: 'Figure',
  ranking: 'Ranking',
  prior_cycle: 'Prior cycle research',
  roster: 'Roster',
  sec_adv: 'SEC ADV',
  disclosure: 'Disclosures',
  assignment: 'Assignment',
  due_diligence: 'Due diligence',
  outreach: 'Outreach',
  metrics: 'Metrics updates'
};

/** survey-api's `ListFieldCycles` in words: which cycle a value belongs to. */
const CYCLE_WORDS: Record<string, string> = {
  latest_response: 'latest response',
  row_cycle: "the response's cycle",
  across_cycles: 'same in every cycle',
  latest_answer: 'latest answer, any cycle',
  registration: 'set at registration',
  registry_now: 'as last checked',
  as_of: 'as of a date',
  prior_cycle: 'prior cycle'
};

/** The prefill chain's sources (`PrefillSources`) in words. */
const PREFILL_WORDS: Record<string, string> = {
  brokercheck: 'BrokerCheck',
  iapd: 'IAPD',
  sec_adv: 'SEC ADV',
  sec_site: 'the SEC site',
  nipr: 'NIPR',
  prior_cycle: 'last cycle',
  cross_survey: 'another survey',
  figures: 'the figures'
};

/** The prefill sources that are registries — what a search finds an answer by ("brokercheck firm");
 *  the others' words ("last cycle") would match names. */
const REGISTRY_PREFILLS: ReadonlySet<string> = new Set(['brokercheck', 'iapd', 'sec_adv', 'sec_site', 'nipr']);

export function originLabel(origin: string): string {
  return wordOf(ORIGIN_WORDS, origin);
}

/** A profile field's questions as one survey asks them: `Last Name (FA-AP-002)`, the first that
 *  holds a value wins, one that counts only while a choice holds marked; null where none. */
function drawWords(questions: readonly IListFieldQuestionRef[]): Nullable<string> {
  if (questions.length === 0) return null;
  return questions
    .map((question) => `${question.number ? `${question.number} ` : ''}${question.label}${question.conditional ? ' (if it applies)' : ''}`)
    .join(', else ');
}

/** What a survey that does not ask a profile field leaves on its rows: what another survey of its
 *  profile group wrote, or nothing. */
function notAskedWords(draw: IListFieldDraw, draws: readonly IListFieldDraw[]): string {
  const others = draws
    .filter((other) => other.profileGroup === draw.profileGroup && other.questions.length > 0)
    .map((other) => listSurveyLabel(other.survey));
  return others.length > 0 ? `filled from ${others.join(', ')} answers` : 'empty on its rows';
}

/**
 * What a field's provenance line says beside its name (SR-420's AC1): its survey — the catalogue's,
 * or on the cross-survey one the surveys that fill it — its cycle, and what an answer pre-fills from
 * or a profile column is filled from. Empty where survey-api sent no provenance.
 */
export function provenanceParts(
  provenance: Nullable<IListFieldProvenance> | undefined,
  survey: string | undefined
): string[] {
  if (!provenance) return [];
  const parts: string[] = [];
  const cross = survey === ALL_SURVEYS.code;
  if (provenance.origin === 'profile' && provenance.drawsOn.length > 0) {
    if (cross) {
      const asking = provenance.drawsOn.filter((draw) => draw.questions.length > 0).map((draw) => listSurveyLabel(draw.survey));
      parts.push(asking.length > 0 ? `from ${asking.join(', ')} answers` : 'not asked in any survey');
    } else {
      const own = provenance.drawsOn.find((draw) => draw.survey === survey) ?? provenance.drawsOn[0];
      const words = drawWords(own.questions);
      parts.push(
        words
          ? `${listSurveyLabel(own.survey)}: ${words}`
          : `not asked in ${listSurveyLabel(own.survey)} — ${notAskedWords(own, provenance.drawsOn)}`
      );
    }
  } else if (survey) {
    parts.push(cross ? 'all surveys' : listSurveyLabel(survey));
  }
  if (provenance.cycle) parts.push(wordOf(CYCLE_WORDS, provenance.cycle));
  if (provenance.prefill.length > 0) {
    const sources = provenance.prefill.map((source) => wordOf(PREFILL_WORDS, source));
    const last = sources.pop();
    parts.push(`pre-filled from ${sources.length > 0 ? `${sources.join(', ')} or ${last}` : last}`);
  }
  return parts;
}

/**
 * A profile field's sources survey by survey (SR-420's AC2) — `FA: 1b Last Name`, `Teams: not
 * asked` — the column's tooltip and the picker's detail. Empty on any other field.
 */
export function provenanceDraws(provenance: Nullable<IListFieldProvenance> | undefined): string[] {
  if (!provenance || provenance.origin !== 'profile') return [];
  return provenance.drawsOn.map(
    (draw) =>
      `${listSurveyLabel(draw.survey)}: ${drawWords(draw.questions) ?? `not asked — ${notAskedWords(draw, provenance.drawsOn)}`}`
  );
}

/** The words a field is also found by in a search: BrokerCheck where it keeps the field, the
 *  registries an answer pre-fills from and, on a profile field, the questions it is filled from —
 *  "firm name" finds Firm. Origin words beside those ("survey", "record") would match whole groups. */
export function provenanceSearchText(provenance: Nullable<IListFieldProvenance> | undefined): string {
  if (!provenance) return '';
  return [
    provenance.origin === 'brokercheck' ? originLabel(provenance.origin) : '',
    ...provenance.drawsOn.flatMap((draw) => draw.questions.map((question) => question.label)),
    ...provenance.prefill.filter((source) => REGISTRY_PREFILLS.has(source)).map((source) => wordOf(PREFILL_WORDS, source))
  ].join(' ');
}

/** The page sizes a list offers (§3.2) — survey-api takes 25 to 1,000. */
export const LIST_PAGE_SIZES: readonly number[] = [25, 50, 100, 250, 500, 1000];

/** The most rows survey-api's Lists read returns at once. */
export const LIST_MAX_PAGE_SIZE = 1000;

/** survey-api's page size where neither the screen nor the view names one (`ListReads`). */
export const DEFAULT_LIST_PAGE_SIZE = 100;

// ---- Values by kind ----------------------------------------------------------------------------

const GROUPED = new Intl.NumberFormat('en-US', { maximumFractionDigits: 6 });

/** An option's label off the field's own options, else the code as spelled. */
export function optionLabel(field: Pick<IListField, 'options'>, code: string): string {
  return field.options?.find((option) => option.code === code)?.label ?? code;
}

/** A count in words: "1 row", "2,000 rows" — `many` where the plural is not the noun with an s. */
export function countOf(n: number, one: string, many = `${one}s`): string {
  return `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`;
}

/** What a removed account's cell says — the hub's spelling. */
export const REMOVED_ACCOUNT = 'an account since removed';

/**
 * A cell's value as drawn, by the field's kind (§2): text as sent; a number grouped; money as the
 * figures draw it; a percent `12.5 %`; a day; an instant in the viewer's zone; Yes / No; an
 * option's label; several labels joined; a person's email (the cell's `label`); a summary's
 * "3 rows · <first>". **Display, never validation**: a value not of its kind is drawn as written
 * (`String`), never dropped. `null` and `undefined` are the empty cell, drawn as nothing (`''`).
 */
export function formatListValue(
  field: Pick<IListField, 'kind' | 'options'>,
  value: unknown,
  label?: Nullable<string>
): string {
  if (value === null || value === undefined) return '';
  switch (field.kind) {
    case 'number':
      return typeof value === 'number' ? GROUPED.format(value) : asWritten(value);
    case 'money':
      return typeof value === 'number' ? formatMoney(value) : asWritten(value);
    case 'percent':
      return typeof value === 'number' ? `${GROUPED.format(value)} %` : asWritten(value);
    case 'date':
      return typeof value === 'string' ? formatDay(value.slice(0, 10)) : asWritten(value);
    case 'datetime':
      return typeof value === 'string' ? formatDateTime(value) : asWritten(value);
    case 'bool':
      return typeof value === 'boolean' ? (value ? 'Yes' : 'No') : asWritten(value);
    case 'enum':
      return typeof value === 'string' ? optionLabel(field, value) : asWritten(value);
    case 'multi_enum':
      if (!Array.isArray(value)) return asWritten(value);
      return value
        .map((code) => (typeof code === 'string' ? optionLabel(field, code) : asWritten(code)))
        .join(', ');
    case 'person':
      return label ?? REMOVED_ACCOUNT;
    case 'summary':
      return summaryText(value);
    default:
      return asWritten(value);
  }
}

function asWritten(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}

/** A grid question's summary, `{n, first}`: "3 rows · <first>". */
function summaryText(value: unknown): string {
  if (typeof value !== 'object' || value === null || !('n' in value)) return asWritten(value);
  const { n, first } = value as { n: unknown; first?: unknown };
  if (typeof n !== 'number') return asWritten(value);
  const rows = `${GROUPED.format(n)} ${n === 1 ? 'row' : 'rows'}`;
  if (first === null || first === undefined || first === '') return rows;
  return `${rows} · ${asWritten(first)}`;
}

// ---- Operators ---------------------------------------------------------------------------------

const OPERATOR_WORDS: Record<string, string> = {
  eq: 'is',
  ne: 'is not',
  in: 'is any of',
  not_in: 'is none of',
  contains: 'contains',
  not_contains: 'does not contain',
  starts: 'starts with',
  ends: 'ends with',
  all_of: 'includes all of',
  gt: 'more than',
  gte: 'at least',
  lt: 'less than',
  lte: 'at most',
  between: 'between',
  empty: 'is blank',
  not_empty: 'is not blank',
  since: 'since',
  before: 'before'
};

/** An operator in words; one this table lacks as spelled. */
export function operatorLabel(op: ListOperator): string {
  return wordOf(OPERATOR_WORDS, op);
}

const BLANKS: readonly ListOperator[] = ['empty', 'not_empty'];

const TEXT_OPS: readonly ListOperator[] = [
  ...BLANKS,
  'eq',
  'ne',
  'in',
  'not_in',
  'contains',
  'not_contains',
  'starts',
  'ends'
];

/** **A mirror** of `ListOperators.For`: the operators a kind takes, the blank pair first; a
 *  summary takes none, and a long text takes text's (SR-408's R4: `Text or LongText`). A kind this
 *  table lacks takes none here, and a stored leaf on it is kept as written by the builder. */
export const OPERATORS_BY_KIND: Readonly<Record<string, readonly ListOperator[]>> = {
  text: TEXT_OPS,
  long_text: TEXT_OPS,
  enum: [...BLANKS, 'eq', 'ne', 'in', 'not_in'],
  person: [...BLANKS, 'eq', 'ne', 'in', 'not_in'],
  multi_enum: [...BLANKS, 'in', 'all_of', 'not_in'],
  number: [...BLANKS, 'eq', 'ne', 'gt', 'gte', 'lt', 'lte', 'between', 'in'],
  money: [...BLANKS, 'eq', 'ne', 'gt', 'gte', 'lt', 'lte', 'between', 'in'],
  percent: [...BLANKS, 'eq', 'ne', 'gt', 'gte', 'lt', 'lte', 'between', 'in'],
  bool: [...BLANKS, 'eq', 'ne'],
  date: [...BLANKS, 'eq', 'since', 'before', 'between'],
  datetime: [...BLANKS, 'eq', 'since', 'before', 'between'],
  summary: []
};

export function operatorsFor(kind: ListFieldKind): readonly ListOperator[] {
  return OPERATORS_BY_KIND[kind] ?? [];
}

/** What a leaf's `value` holds under an operator: nothing, one value, a list (≤ 1,000), or a pair
 *  (`between`). The grammar's, said once for the builder and the chips. */
export type OperatorArity = 'none' | 'one' | 'list' | 'pair';

export function operatorArity(op: ListOperator): OperatorArity {
  if (op === 'empty' || op === 'not_empty') return 'none';
  if (op === 'in' || op === 'not_in' || op === 'all_of') return 'list';
  if (op === 'between') return 'pair';
  return 'one';
}

// ---- Date tokens -------------------------------------------------------------------------------

/** `ListDateTokens.CycleOpen` — resolved per row by survey-api, to the row's cycle's opening. */
export const CYCLE_OPEN = 'cycle_open';

/** **A mirror** of `ListDateTokens`' words that take no `:n`, in its order, with their words. */
export const DATE_WORDS: readonly { token: string; label: string }[] = [
  { token: 'today', label: 'today' },
  { token: 'yesterday', label: 'yesterday' },
  { token: 'tomorrow', label: 'tomorrow' },
  { token: 'this_week', label: 'this week' },
  { token: 'last_week', label: 'last week' },
  { token: 'next_week', label: 'next week' },
  { token: 'this_month', label: 'this month' },
  { token: 'last_month', label: 'last month' },
  { token: 'next_month', label: 'next month' },
  { token: 'this_quarter', label: 'this quarter' },
  { token: 'last_quarter', label: 'last quarter' },
  { token: 'next_quarter', label: 'next quarter' },
  { token: 'this_year', label: 'this year' },
  { token: 'last_year', label: 'last year' },
  { token: 'next_year', label: 'next year' }
];

export interface IDateNForm {
  form: string;
  unit: 'day' | 'week' | 'month';
  shape: 'last' | 'next' | 'ago';
}

/** **A mirror** of `ListDateTokens`' `:n` forms: the form, its unit and how its words read. */
export const DATE_N_FORMS: readonly IDateNForm[] = [
  { form: 'last_n_days', unit: 'day', shape: 'last' },
  { form: 'next_n_days', unit: 'day', shape: 'next' },
  { form: 'n_days_ago', unit: 'day', shape: 'ago' },
  { form: 'last_n_weeks', unit: 'week', shape: 'last' },
  { form: 'next_n_weeks', unit: 'week', shape: 'next' },
  { form: 'n_weeks_ago', unit: 'week', shape: 'ago' },
  { form: 'last_n_months', unit: 'month', shape: 'last' },
  { form: 'next_n_months', unit: 'month', shape: 'next' },
  { form: 'n_months_ago', unit: 'month', shape: 'ago' }
];

/**
 * A date literal read: an ISO day, a word, a `:n` form with its n, or the cycle's opening —
 * `null` for a string that is none of them (kept and drawn as spelled by the caller). The read
 * the token picker opens a stored value with; `writeDateToken` is its other way.
 */
export type DateToken =
  | { kind: 'day'; day: string }
  | { kind: 'word'; token: string }
  | { kind: 'n'; form: string; n: number }
  | { kind: 'cycle-open' };

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

export function readDateToken(text: string): Nullable<DateToken> {
  if (text === CYCLE_OPEN) return { kind: 'cycle-open' };
  if (ISO_DAY.test(text)) return { kind: 'day', day: text };
  if (DATE_WORDS.some((word) => word.token === text)) return { kind: 'word', token: text };
  const [form, count, ...rest] = text.split(':');
  if (rest.length > 0 || count === undefined || !/^\d+$/.test(count)) return null;
  if (!DATE_N_FORMS.some((nForm) => nForm.form === form)) return null;
  return { kind: 'n', form, n: Number(count) };
}

/** A token back as the wire spells it — `{form: last_n_days, n: 30}` is `last_n_days:30`. */
export function writeDateToken(token: DateToken): string {
  switch (token.kind) {
    case 'day':
      return token.day;
    case 'word':
      return token.token;
    case 'n':
      return `${token.form}:${token.n}`;
    case 'cycle-open':
      return CYCLE_OPEN;
  }
}

/** A date literal in words: "the last 30 days", "this quarter", "the cycle opened", a day as the
 *  day; one that reads as no token as spelled. */
export function dateTokenLabel(text: string): string {
  const token = readDateToken(text);
  if (token === null) return text;
  switch (token.kind) {
    case 'day':
      return formatDay(token.day);
    case 'word':
      return DATE_WORDS.find((word) => word.token === token.token)?.label ?? token.token;
    case 'cycle-open':
      return 'the cycle opened';
    case 'n': {
      const nForm = DATE_N_FORMS.find((candidate) => candidate.form === token.form);
      if (nForm === undefined) return text;
      const units = `${GROUPED.format(token.n)} ${token.n === 1 ? nForm.unit : `${nForm.unit}s`}`;
      if (nForm.shape === 'ago') return `${units} ago`;
      return `the ${nForm.shape} ${units}`;
    }
  }
}

// ---- Saved views -------------------------------------------------------------------------------

const VISIBILITY_WORDS: Record<string, string> = {
  private: 'Private',
  shared: 'Shared',
  shared_read_only: 'Shared, read-only'
};

/** `SavedViewVisibilities.All`, in survey-api's order — the choices a save offers. */
export const VISIBILITIES: readonly SavedViewVisibility[] = Object.keys(VISIBILITY_WORDS);

export function visibilityLabel(visibility: SavedViewVisibility): string {
  return wordOf(VISIBILITY_WORDS, visibility);
}

/** A seeded view's mark, and a migrated one's. */
export const SEEDED_MARK = 'Seeded';
export const MIGRATED_MARK = 'From Salesforce';

/**
 * What a migrated view's parts are called, one and many, for the line under its name (§3.3):
 * "3 columns and 1 condition could not be carried". A part this table lacks is drawn as spelled.
 */
const MIGRATED_PART_WORDS: Record<string, readonly [string, string]> = {
  column: ['column', 'columns'],
  filter: ['condition', 'conditions'],
  logic: ['filter logic', 'filter logic'],
  sort: ['sort', 'sorts'],
  group: ['grouping', 'groupings'],
  rule: ['formatting rule', 'formatting rules'],
  owner: ['owner', 'owners'],
  default: ['default', 'defaults']
};

/** A migrated part in words, with its article — "a column", "the filter logic". */
export function migratedPartLabel(part: string): string {
  const words = MIGRATED_PART_WORDS[part];
  if (!words) return part;
  return part === 'logic' || part === 'owner' || part === 'default' ? `the ${words[0]}` : `a ${words[0]}`;
}

/**
 * What survey-api could not carry of a migrated view, counted by part in its order of first
 * appearance — "3 columns and 1 condition". Nothing is counted here but the entries survey-api
 * listed; an empty list says nothing.
 */
export function lostSummary(parts: readonly { part: string }[]): string {
  const counts = new Map<string, number>();
  for (const { part } of parts) counts.set(part, (counts.get(part) ?? 0) + 1);
  const said = [...counts].map(([part, n]) => {
    const words = MIGRATED_PART_WORDS[part];
    const noun = words ? words[n === 1 ? 0 : 1] : part;
    return `${GROUPED.format(n)} ${noun}`;
  });
  if (said.length <= 1) return said[0] ?? '';
  return `${said.slice(0, -1).join(', ')} and ${said[said.length - 1]}`;
}

// ---- States, outcomes, holds, reasons ----------------------------------------------------------

export interface IStateChip {
  label: string;
  tone: ChipTone;
}

const RUN_CHIPS: Record<string, IStateChip> = {
  queued: { label: 'Being written', tone: 'info' },
  done: { label: 'Ready', tone: 'success' },
  failed: { label: 'Failed', tone: 'danger' }
};

/** A report run's state as its chip; one this table lacks as spelled, `neutral`. */
export function runStateChip(state: ReportRunState): IStateChip {
  return RUN_CHIPS[state] ?? { label: state, tone: 'neutral' };
}

const IMPORT_CHIPS: Record<string, IStateChip> = {
  queued: { label: 'Checking the file', tone: 'info' },
  ready: { label: 'Checked, not applied', tone: 'attention' },
  committing: { label: 'Applying…', tone: 'info' },
  committed: { label: 'Applied', tone: 'success' },
  reversing: { label: 'Reversing…', tone: 'info' },
  reversed: { label: 'Reversed', tone: 'neutral' },
  failed: { label: 'Failed', tone: 'danger' }
};

/** `ListImportStates.All`, in survey-api's order — the list's state chips. */
export const IMPORT_STATES: readonly ListImportState[] = Object.keys(IMPORT_CHIPS);

/** An import's state as its chip; one this table lacks as spelled, `neutral`. */
export function importStateChip(state: ListImportState): IStateChip {
  return IMPORT_CHIPS[state] ?? { label: state, tone: 'neutral' };
}

/** "at 10:41", or "on Oct 8, 2026 at 10:41" where the day is not today — the viewer's clock; the
 *  moment a cell was saved by somebody else, said by a conflict on the grid and on the hub alike. */
export function changedWhen(instant: string): string {
  const at = new Date(instant);
  if (Number.isNaN(at.getTime())) return '';
  const time = at.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  if (new Date().toDateString() === at.toDateString()) return `at ${time}`;
  const day = `${at.getFullYear()}-${String(at.getMonth() + 1).padStart(2, '0')}-${String(at.getDate()).padStart(2, '0')}`;
  return `on ${formatDay(day)} at ${time}`;
}

const BATCH_WORDS: Record<string, string> = {
  grid: 'Saved from a list',
  mass_update: 'A mass update',
  import: 'Imported from a file',
  reversal: 'The reversal of an import'
};

/** An edit batch's kind on a history row (§7); one this table lacks as spelled. */
export function batchLabel(kind: string): string {
  return BATCH_WORDS[kind] ?? kind;
}

/** The states an import's record polls in (§5.3): the Worker is still at it. */
export function isImportWorking(state: ListImportState): boolean {
  return state === 'queued' || state === 'committing' || state === 'reversing';
}

const OUTCOME_WORDS: Record<string, string> = {
  matched: 'Matched',
  unmatched: 'Nobody matches this key',
  error: 'Not written — see why'
};

export function outcomeLabel(outcome: ListImportOutcome): string {
  return wordOf(OUTCOME_WORDS, outcome);
}

/** A row's outcome as its chip: matched plain, an error `danger`, anything else `attention`. */
export function importOutcomeChip(outcome: ListImportOutcome): IStateChip {
  const tone: ChipTone = outcome === 'matched' ? 'neutral' : outcome === 'error' ? 'danger' : 'attention';
  return { label: outcomeLabel(outcome), tone };
}

const HOLD_WORDS: Record<string, string> = {
  anomaly: 'Held — an unusual change',
  moved: 'Not written — the value changed before the apply',
  edited: 'Kept — edited after the import'
};

export function holdLabel(hold: ListImportHold): string {
  return wordOf(HOLD_WORDS, hold);
}

const COLUMN_REASON_WORDS: Record<string, string> = {
  'names nothing': 'Skipped: no field has this name',
  'is not importable': 'Skipped: this field is not written from a file',
  'repeats a column': 'Skipped: a second column of this field',
  'needs its as-of date': 'Skipped: the figure needs its as-of date in the heading'
};

/** Why a file's column writes nothing (`ListImportColumns`' reasons), in words. */
export function columnReasonLabel(reason: string): string {
  return wordOf(COLUMN_REASON_WORDS, reason);
}

// ---- Formatting tones --------------------------------------------------------------------------

/** The five colours a view's formatting rule takes (§3.8, the owner's Q4): the status chip's
 *  tones by name, `mock` excluded — it is the demo's. */
export const FORMATTING_TONES: readonly { tone: ChipTone; label: string }[] = [
  { tone: 'info', label: 'blue' },
  { tone: 'success', label: 'green' },
  { tone: 'attention', label: 'amber' },
  { tone: 'danger', label: 'red' },
  { tone: 'neutral', label: 'grey' }
];

/**
 * A rule's stored `tone` as the colour drawn. A migrated rule's tone is Conga's colour stored as
 * sent (`#CCE5FF`): one this table does not name draws `neutral` and says it was not carried,
 * which the Legend lists.
 */
export function formattingTone(stored: Nullable<string>): { tone: ChipTone; carried: boolean } {
  const known = FORMATTING_TONES.find((entry) => entry.tone === stored);
  return known ? { tone: known.tone, carried: true } : { tone: 'neutral', carried: false };
}

/** A tone in words — "blue"; a colour not carried over as "colour not carried over (#CCE5FF)". */
export function formattingToneLabel(stored: Nullable<string>): string {
  const known = FORMATTING_TONES.find((entry) => entry.tone === stored);
  if (known) return known.label;
  return stored ? `colour not carried over (${stored})` : 'grey';
}
